import { DescopeSdkResponse } from "verdi-common/apiTypes/user-profile";
import { DescopeUser } from "verdi-common/schemas/descopeUser/descopeUser";
import { LoraNetworkProfile } from "verdi-common/schemas/loraNetworkProfile/loraNetworkProfile.js";
import { sleep } from "verdi-common/utils/timeoutUtils";
import { BufferedAction, EventHandler } from "../HelperFunctions/index.js";
import { AreaOfInterest } from "../Models/AreaOfInterest.js";
import { ClientLoraNetworkProfile } from "../Models/ClientLoraNetworkProfile.js";
import { BlockValve } from "../Models/Devices/BlockValve.js";
import { IrrigationDevice } from "../Models/Devices/IrrigationDevice.js";
import { IrrigationDeviceBase } from "../Models/Devices/IrrigationDeviceBase.js";
import { SensoterraMoistureSensor } from "../Models/Devices/SensoterraMoistureSensor.js";
import { ThirdPartyDeviceBase } from "../Models/Devices/ThirdPartyDeviceBase.js";
import {
    addEntry,
    clearIndex,
    deleteEntry,
    findEventually,
    IrrigationSet,
    MasterIndex,
    User,
    Zone,
} from "../Models/index.js";
import { NDVIOverlay } from "../Models/Overlay/NDVIOverlay.js";
import { CommandGroup } from "../Models/Scheduling/commandGroup.js";
import { RepeatingIrrigationEvent } from "../Models/Scheduling/RepeatingIrrigationEvent.js";

type JWTResponse = {
    sessionJwt: string;
    refreshJwt?: string;
    cookieDomain?: string;
    cookiePath?: string;
    cookieMaxAge?: number;
    cookieExpiration?: number;
    user?: DescopeUser;
    firstSeen?: boolean;
    sessionExpiration: number;
};

interface SessionMethods {
    getRefreshToken: () => string;
    getSessionToken: () => string;
    isSessionTokenExpired: (token?: string) => boolean;
    refresh: (token?: string) => Promise<DescopeSdkResponse<JWTResponse>>;
}

export class SessionHandler {
    static endpoint: string = undefined;
    static _pendingSessionRefresh: Promise<DescopeSdkResponse<JWTResponse>> | null = null;
    static sessionMethods: SessionMethods = {
        getRefreshToken: (): string => "",
        getSessionToken: (): string => "",
        isSessionTokenExpired: (_token?: string) => false,
        refresh: (_token?: string) => Promise.resolve({} as DescopeSdkResponse<JWTResponse>),
    };

    /**
     *
     * @type {EventHandler}
     */
    static onSessionLoad = new EventHandler();
    /**
     *
     * @type {EventHandler}
     */
    static onSessionUnload = new EventHandler();

    /**
     *
     * @type {{addEntry: addEntry, deleteEntry: deleteEntry, MasterIndex: {}, findEventually: findEventually}}
     */
    static session = undefined;

    /**
     *
     * @type {EventHandler}
     */
    static onLogOut = new EventHandler();

    /**
     *
     * @type {EventHandler}
     */
    static onLogIn = new EventHandler();

    /**
     * Default map position
     * @type {number[]}
     */
    static defaultPosition = [49.825171, -119.464043];

    /**
     * A list of all accounts that this account has access to
     * @type {{name: String, id: string, adminUseOnly?: boolean}[]}
     */
    static accounts = [];

    /**
     *
     * @type {boolean}
     */
    static admin = false;

    /**
     *
     * @type {boolean}
     */
    static loggedIn = undefined;

    /**
     * ID of the current user
     * @type {string | undefined}
     */
    static curUser?: string = undefined;

    /**
     *
     * @type {User}
     */
    static currentUserObject: User = undefined;

    static async whoAmI(): Promise<Response> {
        return this.doFetch("WhoAmI", {
            headers: {
                "Content-Type": "application/json",
            },
            method: "GET",
            redirect: "follow",
        });
    }

    static async logOut() {
        this.loggedIn = false;
        this.admin = false;
        this.curUser = undefined;
        this.currentUserObject = undefined;
        this.accounts = [];
        this.defaultPosition = [49.825171, -119.464043];
        return this.onLogOut.trigger({});
    }

    static async logIn(oauthUser: DescopeUser) {
        this.admin = oauthUser.roleNames?.includes("Verdi Administrator") ?? false;

        try {
            const response = await this.whoAmI();
            const responseData = response.ok ? await response.json() : null;
            if (responseData) {
                this.accounts = responseData.accounts ?? this.accounts;
                this.curUser = responseData.user._id;
                const coords = responseData.user.defaultCoordinates;
                if (coords?.lat !== undefined && coords?.lng !== undefined) {
                    this.defaultPosition = [coords.lat, coords.lng];
                }
            }

            this.loggedIn = true;

            await this.onLogIn.trigger({
                admin: this.admin,
                accounts: this.accounts,
                defaultPos: this.defaultPosition,
                adminUsername: responseData?.adminUsername ?? undefined,
                // Descope user — used by PostHog identify (person is the human, not the org).
                descopeUser: {
                    userId: oauthUser.userId,
                    email: oauthUser.email,
                    name: oauthUser.name,
                    roleNames: oauthUser.roleNames,
                },
            });
        } catch (error) {
            console.error("Error fetching user info:", error);
            await this.logOut();
        }
    }

    /** Normalize HeadersInit (Headers | string[][] | Record<string,string> | undefined)
     *  into a plain Record so callers can predict the interface when updating headers. */
    private static getHeadersAsRecord(headersInit: HeadersInit | undefined): Record<string, string> {
        const record: Record<string, string> = {};
        if (headersInit instanceof Headers) {
            headersInit.forEach((value, key) => {
                record[key] = value;
            });
        } else if (Array.isArray(headersInit)) {
            headersInit.forEach(([key, value]) => {
                record[key] = value;
            });
        } else if (headersInit) {
            Object.assign(record, headersInit);
        }
        return record;
    }

    static async doFetch(url: string, options: RequestInit) {
        const headers = SessionHandler.getHeadersAsRecord(options.headers);

        // When a session token is available:
        // - synchronously refresh it if expired, throwing if the refresh fails,
        // - set it in the Authorization header as the Bearer token
        //
        // When no session token is present, the request proceeds unauthenticated.
        //
        // Although manual synchronous refresh is infrequently required given our
        // <AuthProvider /> manages automatic refreshes in a React context, this
        // `doFetch` method is also called by background threads that may not
        // align with automatic refreshes.
        let sessionToken = this.sessionMethods.getSessionToken();
        if (sessionToken) {
            if (this.sessionMethods.isSessionTokenExpired(sessionToken)) {
                // Deduplicate concurrent refresh attempts: if a refresh is already
                // pending, all callers share one promise instead of issuing separate
                // requests.
                if (!SessionHandler._pendingSessionRefresh) {
                    const refreshToken = this.sessionMethods.getRefreshToken();
                    SessionHandler._pendingSessionRefresh = this.sessionMethods.refresh(refreshToken).finally(() => {
                        SessionHandler._pendingSessionRefresh = null;
                    });
                }
                const refreshed = await SessionHandler._pendingSessionRefresh;
                sessionToken = refreshed.data?.sessionJwt;
                if (!refreshed.ok || !sessionToken) {
                    throw new Error("Failed to refresh session token.");
                }
            }
            headers.Authorization = `Bearer ${sessionToken}`;
        }
        if (this.curUser) {
            headers.User = this.curUser;
        }

        const result = await fetch(this.endpoint + url, { ...options, headers });
        if (result.status === 401 && this.loggedIn) {
            this.on401();
            throw new Error("unauthorized");
        } else if (result.ok) {
            return result;
        } else {
            let errorMessage;
            // Parse error message from response
            try {
                const errorJson = await result.json();
                errorMessage = errorJson.message ?? errorJson.error ?? "Unknown Error";
            } catch {
                errorMessage = "Unknown Error";
            }
            throw { status: result.status, message: errorMessage };
        }
    }

    static numOf401s = 0;
    static on401() {
        this.numOf401s++;
        const temp = this.numOf401s;
        setTimeout(async () => {
            if (this.numOf401s > 20) {
                this.numOf401s = 0;
                await this.logOut();
                this.numOf401s = 0;
            } else if (this.numOf401s === temp) {
                this.numOf401s = 0;
            }
        }, 800);
    }

    /**
     *
     * @type {Record<string, {value: number, date: Date}>}
     */
    static recentData = {};
    static lastLoadedRecentData = new Date(0);
    static currentlyReloadingRecentData = false;
    static onRecentDataUpdated = new EventHandler();
    static reloadRecentDataAction = new BufferedAction(
        () => {
            SessionHandler.reloadRecentData();
        },
        60 * 1000 * 4,
        true,
        false,
    );
    static async reloadRecentData() {
        if (this.currentlyReloadingRecentData) {
            while (this.currentlyReloadingRecentData) {
                await sleep(50);
            }
            return;
        }
        if (Date.now() - this.lastLoadedRecentData.valueOf() < 1000 * 60 * 3) {
            return;
        }
        this.currentlyReloadingRecentData = true;
        const response = await this.doFetch(`recentData`, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                compress: "true",
            },
            method: "GET",
            redirect: "follow",
        });
        const newRecentData = await response.json();
        this.recentData = newRecentData;
        const idsUpdated = new Set();
        //the set of devices that need to be refreshed because their valve state changed.
        const needsActualRefresh = new Set();
        Object.keys(MasterIndex).forEach((k) => {
            MasterIndex[k].all.forEach((item) => {
                if (item.checkForRecentData) {
                    Object.keys(newRecentData)
                        .filter((k) => k.includes(item.id))
                        .forEach((k) => {
                            const friendlyK = k.split("__")[0];
                            newRecentData[k].date = new Date(newRecentData[k].date);
                            //newRecentData[friendlyK] = newRecentData[k];
                            //if the valve state changed, we add this device to the set of devices needing a real refresh
                            if (
                                friendlyK === "valveState" &&
                                item instanceof IrrigationDeviceBase &&
                                item.recentData[friendlyK]?.value !== newRecentData[k].value
                            ) {
                                needsActualRefresh.add(item);
                            }

                            //if the key has changed value or date, we update it.
                            if (
                                !item.recentData[friendlyK] ||
                                newRecentData[k].date.valueOf() > (item.recentData[friendlyK]?.date?.valueOf() || 0)
                            ) {
                                item.recentData[friendlyK] = newRecentData[k];
                                idsUpdated.add(item.id);
                            }
                        });
                }
            });
        });
        //do full refreshes on subset of devices that need it.
        console.info(`soft refresh found ${needsActualRefresh.size} device(s) in need of full refresh`);
        const promises = [];
        needsActualRefresh.forEach((d) => {
            // @ts-ignore
            if (d.refresh) {
                // @ts-ignore
                promises.push(d.refresh());
            }
        });
        Promise.allSettled(promises);
        this.currentlyReloadingRecentData = false;
        this.lastLoadedRecentData = new Date(Date.now());
        //up to date!
        this.onRecentDataUpdated.trigger({
            affectedDevices: idsUpdated,
        });
    }
    /**
     * Loads the master index with content from the current account
     * @return {Promise<any>}
     */
    static async loadSession() {
        if (this.session) {
            await this.unloadSession();
        }
        this.session = {
            MasterIndex: MasterIndex,
            findEventually: findEventually,
            deleteEntry: deleteEntry,
            addEntry: addEntry,
        };
        const response = await this.doFetch("allData", {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                compress: "true",
            },
            method: "GET",
            redirect: "follow",
        });
        const allData = await response.json();

        const awaitQueue = [];
        if (allData.defaultCoordinates?.lat !== undefined && allData.defaultCoordinates?.lng !== undefined) {
            this.defaultPosition = [allData.defaultCoordinates.lat, allData.defaultCoordinates.lng];
        } else {
            console.error("Error: No default position for this user!");
        }

        /**
         * The following is initializing all of the new objects we are loading from the server. Some of these
         * operations look like no-ops, but the side effect of something
         * like `new ThirdPartyDeviceBase(thirdPartyDeviceJSON);` is that the new ThirdPartyDevice is added to the Master
         * Index. If these calls are removed, other downstream tooling will no longer be able to find these models.
         *
         * Be ye warned, the below code be cursed beyond recognition. If ye dare optimize here, only bugs will ye find.
         */
        if (allData.CurUser) {
            this.currentUserObject = new User(allData.CurUser);
        }

        (allData.RecentData || []).forEach((recentData) => {
            this.recentData = recentData.data;
            this.lastLoadedRecentData = new Date(Date.now());
        });
        (allData.Zones || []).forEach((zoneJSON) => {
            const c = new Zone(zoneJSON);
            awaitQueue.push(c.loadPromise);
        });
        while (awaitQueue.length > 0) {
            await awaitQueue.pop();
        }
        (allData.IrrigationSets || []).forEach((irrigationSetJSON) => {
            try {
                const c = new IrrigationSet(irrigationSetJSON);
                awaitQueue.push(c.loadPromise);
            } catch (e) {
                console.warn(e);
            }
        });
        (allData.ThirdPartyDevices || []).forEach((thirdPartyDeviceJSON) => {
            try {
                const c = new ThirdPartyDeviceBase(thirdPartyDeviceJSON);
                awaitQueue.push(c.loadPromise);
            } catch (e) {
                console.warn(e);
            }
        });
        (allData.Devices || []).forEach((devJSON) => {
            try {
                const deviceType = (devJSON.DeviceType || "").toLowerCase();
                let c;
                if (deviceType.includes("block")) {
                    c = new BlockValve(devJSON);
                } else if (deviceType === "msense") {
                    c = new SensoterraMoistureSensor(devJSON);
                } else {
                    c = new IrrigationDevice(devJSON);
                }
                awaitQueue.push(c.loadPromise);
            } catch (e) {
                console.warn(e);
            }
        });
        (allData.Images || []).forEach((imageJSON) => {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const c = new NDVIOverlay(imageJSON);
            } catch (e) {
                console.warn(e);
            }
        });
        (allData.Users || []).forEach((userJSON) => {
            const c = new User(userJSON);
            awaitQueue.push(c.loadPromise);
        });
        (allData.RepeatingIrrigations || []).forEach((RepeatingIrrigationJSON) => {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const c = new RepeatingIrrigationEvent(RepeatingIrrigationJSON);
            } catch (e) {
                console.warn(e);
            }
        });
        (allData.SensoterraDevices || []).forEach((SensoterraJSON) => {
            try {
                const c = new SensoterraMoistureSensor(SensoterraJSON);
                awaitQueue.push(c.loadPromise);
            } catch (e) {
                console.warn(e);
            }
        });
        (allData.AreasOfInterest || []).forEach((aoiJSON) => {
            try {
                const c = new AreaOfInterest(aoiJSON);
                awaitQueue.push(c.loadPromise);
            } catch (e) {
                console.warn(e);
            }
        });
        (allData.CommandGroups || []).forEach((CommandGroupJSON) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const c = new CommandGroup(CommandGroupJSON);
        });
        (allData.LoraNetworkProfiles || []).forEach((loraNetworkProfile) => {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const c = new ClientLoraNetworkProfile(loraNetworkProfile as LoraNetworkProfile);
            } catch (e) {
                console.warn(e);
            }
        });

        awaitQueue.push(sleep(500));
        while (awaitQueue.length > 0) {
            await awaitQueue.pop().catch((e) => {
                console.warn("error loading asset");
                console.warn(e);
            });
        }

        const linkContainingAOI = (dev) => {
            let containingAOI = undefined;
            for (let i = 0; i < MasterIndex.aoi.all.length; i++) {
                const testAOI = MasterIndex.aoi.all[i];
                if (testAOI.containsPoint(dev.lat, dev.long)) {
                    containingAOI = testAOI;
                    dev.linkedAreaOfInterest = testAOI;
                    break;
                }
            }
            dev.linkedAreaOfInterest = containingAOI;
        };
        MasterIndex.SensoterraMoisture.all.forEach(linkContainingAOI);
        MasterIndex.irrigationDevice.all.forEach(linkContainingAOI);
        MasterIndex.blockValve.all.forEach(linkContainingAOI);
        MasterIndex.thirdPartyDevice.all.forEach(linkContainingAOI);

        MasterIndex.zone.all.forEach((zone) => {
            let containingAOI = undefined;
            for (let j = 0; j < zone.polygon.length; j++) {
                if (containingAOI) {
                    break;
                }
                const point = zone.polygon[j];
                for (let i = 0; i < MasterIndex.aoi.all.length; i++) {
                    const testAOI = MasterIndex.aoi.all[i];
                    if (testAOI.containsPoint(point.lat, point.lng)) {
                        containingAOI = testAOI;
                        break;
                    }
                }
            }
            zone.linkedAreaOfInterest = containingAOI;
            if (containingAOI) {
                containingAOI.zonesInArea.push(zone);
            }
        });

        this.onSessionLoad.trigger({ handler: this }).catch((e) => {
            console.warn(e);
        });
        return allData;
    }

    /**
     * Clears the master index, unloads the present session, and waits for everything to be cleaned up
     * @return {Promise<void>}
     */
    static async unloadSession() {
        if (!this.session) {
            return;
        }
        const thingsToAwait = [];
        thingsToAwait.push(clearIndex());
        thingsToAwait.push(this.onSessionUnload.trigger({}));
        thingsToAwait.push(sleep(700));
        await thingsToAwait[0];
        await thingsToAwait[1];
        await thingsToAwait[2];
        this.session = undefined;
        this.onSessionUnload.trigger({ handler: this }).catch((e) => {
            console.warn(e);
        });
    }

    /**
     * Changes to a new user account for the admin to be logged in under
     * @param newUserID
     * @return {Promise<void>}
     */
    static async changeUser(newUserID: string) {
        console.debug(`Switching account from ${this.curUser} to ${newUserID}...`);
        await this.unloadSession();
        this.curUser = newUserID;
        await this.loadSession();
    }
}
